import React, { useState, useEffect } from "react";
import {
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Button,
  Table,
  Drawer,
  Stack,
  Tabs,
  LoadingOverlay,
  SimpleGrid,
  Image,
  Divider,
  ScrollArea,
  Select,
  Box,
  ThemeIcon,
  ActionIcon,
  Center,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconEye,
  IconBuildingStore,
  IconReceipt2,
  IconWorldWww,
  IconBuildingBank,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const ADMIN_API = `${API_BASE_URL}/`;
const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

export default function BoothApplicationReview() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("PENDING");

  const [reviewOpen, setReviewOpen] = useState(false);
  const [appDetails, setAppDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    fetch(`${ADMIN_API}admin/events`, { headers: getAdminHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          const formattedEvents = data.events.map((e) => ({
            value: e.id,
            label: e.title,
          }));
          setEvents(formattedEvents);
          if (formattedEvents.length > 0)
            setSelectedEventId(formattedEvents[0].value);
        }
      })
      .catch(() => showError("Error", "Failed to load events"));
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;

    setLoading(true);
    fetch(
      `${ADMIN_API}admin/events/${selectedEventId}/applications?status=${activeTab}`,
      {
        headers: getAdminHeaders(),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setApplications(data.applications);
      })
      .catch(() => showError("Error", "Failed to fetch applications."))
      .finally(() => setLoading(false));
  }, [selectedEventId, activeTab]);

  const handleReviewClick = async (appId) => {
    setReviewOpen(true);
    setDetailsLoading(true);
    try {
      const res = await fetch(
        `${ADMIN_API}shop-owner/onboarding/applications/${appId}`,
        {
          headers: getAdminHeaders(),
        },
      );
      const data = await res.json();
      if (data.success) {
        setAppDetails(data);
      } else {
        showError("Error", "Failed to load application details.");
        setReviewOpen(false);
      }
    } catch (e) {
      showError("Error", "Network error.");
      setReviewOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleApprove = async (appId) => {
    try {
      const res = await fetch(
        `${ADMIN_API}shop-owner/admin/onboarding/approve`,
        {
          method: "POST",
          headers: getAdminHeaders(),
          body: JSON.stringify({ applicationId: appId }),
        },
      );
      const data = await res.json();

      if (data.success) {
        showSuccess(
          "Approved!",
          "ShopFront created and products linked securely.",
        );
        setReviewOpen(false);
        setApplications((prev) => prev.filter((a) => a.id !== appId));
      } else {
        showError("Approval Failed", data.error);
      }
    } catch (e) {
      showError("Error", "Network error during approval.");
    }
  };

  const handleReject = async (appId) => {
    try {
      const res = await fetch(
        `${ADMIN_API}shop-owner/admin/onboarding/reject`,
        {
          method: "POST",
          headers: getAdminHeaders(),
          body: JSON.stringify({ applicationId: appId }),
        },
      );
      const data = await res.json();

      if (data.success) {
        showSuccess("Rejected", "The application has been declined.");
        setReviewOpen(false);
        setApplications((prev) => prev.filter((a) => a.id !== appId));
      } else {
        showError("Rejection Failed", data.error);
      }
    } catch (e) {
      showError("Error", "Network error.");
    }
  };

  return (
    <Stack gap="xl" px={{ base: "xs", sm: "md" }}>
      {/* HEADER SECTION */}
      <Paper withBorder p="xl" radius="lg" shadow="sm" bg="gray.0">
        <Group justify="space-between" align="center" wrap="wrap" gap="lg">
          <Box>
            <Title order={2} size="h1" mb={4}>
              Event Booth Approvals
            </Title>
            <Text size="lg" c="dimmed">
              Select an active event to review and approve merchant
              applications.
            </Text>
          </Box>

          <Select
            label={
              <Text size="sm" fw={600} mb={4}>
                Filter by Target Event
              </Text>
            }
            placeholder="Select an event..."
            data={events}
            value={selectedEventId}
            onChange={setSelectedEventId}
            searchable
            size="lg"
            w={{ base: "100%", sm: 350 }}
            radius="md"
          />
        </Group>
      </Paper>

      {/* MAIN CONTENT AREA */}
      {selectedEventId ? (
        <Paper withBorder radius="lg" shadow="sm" pos="relative">
          <LoadingOverlay
            visible={loading}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />

          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            color="blue"
            radius="md"
          >
            <Tabs.List px="md" pt="md">
              <Tabs.Tab value="PENDING">
                <Text size="md" fw={600}>
                  Pending Queue
                </Text>
              </Tabs.Tab>
              <Tabs.Tab value="APPROVED">
                <Text size="md" fw={600}>
                  Approved Booths
                </Text>
              </Tabs.Tab>
              <Tabs.Tab value="REJECTED">
                <Text size="md" fw={600}>
                  Rejected
                </Text>
              </Tabs.Tab>
            </Tabs.List>

            <Box p="md" mih={400}>
              <Table
                verticalSpacing="md"
                horizontalSpacing="md"
                highlightOnHover
              >
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th>
                      <Text size="sm" tt="uppercase" c="dimmed">
                        Brand Name
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="sm" tt="uppercase" c="dimmed">
                        Owner Email
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="sm" tt="uppercase" c="dimmed">
                        Type
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="sm" tt="uppercase" c="dimmed">
                        Date Submitted
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="sm" tt="uppercase" c="dimmed">
                        Status
                      </Text>
                    </Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {applications.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={6} ta="center" c="dimmed" py="xl">
                        <Text size="lg">
                          No applications found in this queue.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    applications.map((app) => (
                      <Table.Tr key={app.id}>
                        <Table.Td>
                          <Text fw={700} size="md">
                            {app.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{app.user?.email || "Unknown"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="gray" variant="light" size="sm">
                            {app.setupType}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(app.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={
                              app.status === "PENDING"
                                ? "orange"
                                : app.status === "APPROVED"
                                  ? "green"
                                  : "red"
                            }
                            variant="light"
                            size="md"
                          >
                            {app.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            variant="light"
                            size="sm"
                            leftSection={<IconEye size={16} />}
                            onClick={() => handleReviewClick(app.id)}
                          >
                            Review Details
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Box>
          </Tabs>
        </Paper>
      ) : (
        <Paper withBorder p="xl" ta="center" radius="lg" bg="gray.0" mih={300}>
          <Center h="100%">
            <Stack align="center" gap="sm">
              <ThemeIcon size={64} radius="xl" color="gray" variant="light">
                <IconBuildingStore size={32} />
              </ThemeIcon>
              <Text size="xl" fw={600} c="dimmed">
                Please select an event from the dropdown above to view
                applications.
              </Text>
            </Stack>
          </Center>
        </Paper>
      )}

      {/* REVIEW DRAWER */}
      <Drawer
        opened={reviewOpen}
        onClose={() => setReviewOpen(false)}
        position="right"
        size="xl" // Made larger to fit the card-based layout
        title={
          <Title order={3} size="h2">
            Application Review
          </Title>
        }
        padding="xl"
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        <Box pos="relative" mih={500}>
          <LoadingOverlay
            visible={detailsLoading}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />

          {appDetails && (
            <Stack gap="xl">
              {/* Brand Identity Card */}
              <Paper withBorder p="lg" radius="md" bg="gray.0">
                <Text size="sm" tt="uppercase" c="dimmed" fw={700} mb="sm">
                  Store Identity
                </Text>
                <Group align="start" wrap="nowrap">
                  <Image
                    src={
                      appDetails.application.logoUrl ||
                      "https://placehold.co/100"
                    }
                    w={80}
                    h={80}
                    radius="md"
                    style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                  />
                  <Box style={{ flex: 1 }}>
                    <Title order={3} size="h2">
                      {appDetails.application.name}
                    </Title>
                    <Group gap="xs" mt={4} mb="sm">
                      <Badge variant="dot" color="blue" size="md">
                        {appDetails.application.setupType}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dark.7">
                      {appDetails.application.description ||
                        "No description provided."}
                    </Text>
                  </Box>
                </Group>
              </Paper>

              {/* Payout Routing Card */}
              <Box>
                <Title order={4} size="h3" mb="sm">
                  Requested Payout Method
                </Title>
                <Paper withBorder p="lg" radius="md">
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                    <Box>
                      <Group gap="xs" mb={4}>
                        <IconReceipt2
                          size={16}
                          color="var(--mantine-color-blue-6)"
                        />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                          Razorpay Gateway ID
                        </Text>
                      </Group>
                      <Text
                        fw={600}
                        size="lg"
                        c={
                          appDetails.application.razorpayAccountId
                            ? "blue.9"
                            : "dimmed"
                        }
                      >
                        {appDetails.application.razorpayAccountId ||
                          "Not Provided"}
                      </Text>
                    </Box>
                    <Box>
                      <Group gap="xs" mb={4}>
                        <IconBuildingBank
                          size={16}
                          color="var(--mantine-color-gray-6)"
                        />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                          Manual Bank / UPI
                        </Text>
                      </Group>
                      <Text
                        fw={600}
                        size="lg"
                        c={
                          appDetails.application.bankAccountNumber ||
                          appDetails.application.upiId
                            ? "dark.9"
                            : "dimmed"
                        }
                      >
                        {appDetails.application.bankAccountNumber ||
                          appDetails.application.upiId ||
                          "Not Provided"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Paper>
              </Box>

              <Divider color="gray.3" />

              {/* Initial Catalog Preview */}
              <Box>
                <Group justify="space-between" align="center" mb="md">
                  <Title order={4} size="h3">
                    Initial Catalog Preview
                  </Title>
                  <Badge size="lg" color="blue" variant="light">
                    {appDetails.pendingProducts?.length || 0} Items
                  </Badge>
                </Group>

                <ScrollArea h={350} type="auto" offsetScrollbars>
                  <Stack gap="md">
                    {appDetails.pendingProducts?.length === 0 ? (
                      <Text c="dimmed" fs="italic">
                        No products added yet.
                      </Text>
                    ) : (
                      appDetails.pendingProducts?.map((product) => (
                        <Paper
                          key={product.id}
                          withBorder
                          p="md"
                          radius="md"
                          className="hover:bg-gray-50"
                        >
                          <Group
                            justify="space-between"
                            align="center"
                            wrap="nowrap"
                          >
                            <Box style={{ flex: 1 }}>
                              <Text fw={700} size="lg">
                                {product.title}
                              </Text>
                              <Text size="sm" c="dimmed" mt={4}>
                                Base Price:{" "}
                                <Text span fw={600} c="dark.9">
                                  ₹{product.unitPrice}
                                </Text>
                              </Text>
                            </Box>
                            <Stack align="flex-end" gap={4}>
                              <Badge
                                color={
                                  product.status === "SUSPENDED"
                                    ? "orange"
                                    : "gray"
                                }
                                variant="light"
                              >
                                {product.status}
                              </Badge>
                              <Text size="xs" c="dimmed">
                                {product.variants?.length || 0} Variants
                              </Text>
                            </Stack>
                          </Group>
                        </Paper>
                      ))
                    )}
                  </Stack>
                </ScrollArea>
              </Box>

              {/* ACTION BUTTONS (Only for Pending) */}
              {appDetails.application.status === "PENDING" && (
                <Box
                  mt="auto"
                  pt="xl"
                  style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
                >
                  <Text size="sm" ta="center" c="dimmed" mb="md">
                    Approving this application will generate their live
                    ShopFront and link all pending products.
                  </Text>
                  <Group grow>
                    <Button
                      size="xl"
                      variant="light"
                      color="red"
                      onClick={() => handleReject(appDetails.application.id)}
                    >
                      Reject Application
                    </Button>
                    <Button
                      size="xl"
                      color="green"
                      onClick={() => handleApprove(appDetails.application.id)}
                    >
                      Approve & Launch Shop
                    </Button>
                  </Group>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Stack>
  );
}
